import { useRef, HTMLInputTypeAttribute, ChangeEvent, ReactNode } from 'react';
import { TextField as MUITextField, TextFieldProps as MuiTextFieldProps } from '@mui/material';
import { Control, Controller, FieldError, FieldValues, Path } from 'react-hook-form';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import { LabelWithTooltip } from '@/components/forms/other/LabelWithTooltip/LabelWithTooltip';
import { JSXFromString } from '@/utils/JSXFromString';
import { applyMaskToString, MaskDefinition } from '@/utils/mask/mask';

interface CustomTextFieldProps<T extends FieldValues> {
    control: Control<T>;
    name: Path<T>;
    label?: string | number;
    error?: FieldError | null;
    hideError?: boolean;
    type?: HTMLInputTypeAttribute;
    disableTranslation?: boolean;
    endAdornment?: ReactNode;
    mask?: string;
    maskDefinition?: MaskDefinition;
}

export type TextFieldProps<T extends FieldValues> = CustomTextFieldProps<T> &
    Omit<MuiTextFieldProps, 'error' | 'label' | 'name' | 'helperText'>;

export const TextField = <T extends FieldValues>(props: TextFieldProps<T>) => {
    const { t } = usePhrase();
    const {
        control,
        name,
        type,
        label,
        placeholder,
        error,
        onChange,
        hideError,
        endAdornment,
        mask,
        maskDefinition,
        slotProps,
        ...other
    } = props;
    const textfieldRef = useRef<HTMLDivElement>(null);
    const labelRef = useRef<HTMLLabelElement>(null);

    // disable label animation on load
    textfieldRef.current?.classList.add('label-noanim');

    const getErrorText = (fieldError: FieldError | undefined) => {
        if (hideError) {
            return '';
        }
        const message = fieldError?.message || error?.message;
        if (message) {
            if (typeof message === 'number') {
                return t(message);
            } else {
                return JSXFromString(message) as ReactNode;
            }
        }
        return undefined;
    };

    return (
        <Controller
            name={name}
            control={control}
            render={({
                field: { value, onChange: defaultOnChange, onBlur, ref },
                fieldState: { error: fieldError },
            }) => {
                const inputValue = mask
                    ? applyMaskToString({ value, mask, maskDefinition })
                    : (value ?? '');

                const mergedSlotProps = {
                    ...slotProps,
                    input: {
                        ...slotProps?.input,
                        // @ts-ignore endAdornment exists
                        endAdornment: endAdornment || (slotProps?.input?.endAdornment as ReactNode),
                    },
                    htmlInput: {
                        ...slotProps?.htmlInput,
                        maxLength:
                            // @ts-ignore maxLength exists
                            mask?.length || (slotProps?.htmlInput?.maxLength as number | undefined),
                    },
                    inputLabel: {
                        ...slotProps?.inputLabel,
                        ref: labelRef,
                    },
                };

                const labelWithTooltip =
                    label && !other.hiddenLabel ? (
                        <LabelWithTooltip label={label} labelRef={labelRef} />
                    ) : (
                        ''
                    );

                const inputPlaceholder = mask?.toUpperCase() || placeholder;

                const handleFocus = () => {
                    const element = textfieldRef.current;
                    if (element?.classList.contains('label-noanim')) {
                        element.classList.remove('label-noanim');
                    }
                };

                const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
                    const newValue = mask
                        ? applyMaskToString({ value: event.target.value, mask, maskDefinition })
                        : event.target.value;
                    defaultOnChange(newValue);

                    if (onChange) {
                        event.target.value = newValue;
                        onChange(event);
                    }
                };

                return (
                    <MUITextField
                        name={name}
                        value={inputValue}
                        ref={textfieldRef}
                        onChange={handleChange}
                        onBlur={onBlur}
                        onFocus={handleFocus}
                        type={type || 'text'}
                        label={labelWithTooltip}
                        error={Boolean(error || fieldError)}
                        inputRef={ref}
                        helperText={getErrorText(fieldError)}
                        placeholder={inputPlaceholder}
                        {...other}
                        slotProps={mergedSlotProps}
                    />
                );
            }}
        />
    );
};
